import React, { useEffect, useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getPerformanceEvaluationCampaignById, PerformanceEvaluationCampaign, saveEvaluationResponse, getPerformanceEvaluationCollaboratorByUserEmail, getEvaluationResponse, trackEvaluationInviteOpened } from '@/features/People/Evaluations/services/evaluationsService';
import { CriteriaGroup, CriteriaBehavior, CriteriaIndicator, getCriteriaGroups } from '../../../PerformanceCriteria/services/criteriaService';
import { EvaluationScale, getEvaluationScales } from '../../../PerformanceCriteria/services/scalesService';
import { getQuestionnaireById, saveQuestionnaire, Questionnaire } from '@/features/People/Evaluations/services/questionnairesService';
import { useToast } from '@/hooks/useToast';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import ProfileHeader from '../../Shared/ProfileHeader/ProfileHeader';

interface WizardStep {
    type: 'QUESTION' | 'INTRODUCTION' | 'CONCLUSION';
    content?: string; // For Intro/Conclusion

    // For Questions
    groupTitle?: string;
    behaviorTitle?: string;
    indicatorId: string;
    indicatorDescription?: string;
    question?: string;
    scale?: EvaluationScale;
}

const EditableQuestionText = ({ initialValue, onSave }: { initialValue: string, onSave: (val: string) => void }) => {
    const [text, setText] = useState(initialValue);

    useEffect(() => {
        setText(initialValue);
    }, [initialValue]);

    return (
        <textarea
            className="w-full text-2xl md:text-3xl font-bold text-gray-800 leading-tight border-b-2 border-transparent hover:border-gray-200 focus:border-indigo-500 focus:ring-0 bg-transparent resize-none overflow-hidden transition-all"
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={() => {
                if (text !== initialValue) {
                    onSave(text);
                }
            }}
        />
    );
};

export const EvaluationWizardPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { user } = useAuth();
    const view = searchParams.get('view') === 'LEADER' ? 'LEADER' : 'SELF';
    const questionnaireId = searchParams.get('questionnaireId');

    const [campaign, setCampaign] = useState<PerformanceEvaluationCampaign | null>(null);
    const [collaborator, setCollaborator] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [currentStep, setCurrentStep] = useState(0);
    const [answers, setAnswers] = useState<Record<string, number>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [responseId, setResponseId] = useState<string | undefined>(undefined);

    useEffect(() => {
        const fetchCampaignAndResponse = async () => {
            if (!id) return;

            setLoading(true);
            setAnswers({});
            setResponseId(undefined);
            setCurrentStep(0);

            // Allow testing 'preview' with a fake collaborator
            const currentCollaboratorId = collaborator?.id || 'preview-collab';

            try {
                let campaignData: PerformanceEvaluationCampaign | null = null;

                if (id === 'preview') {
                    // Preview Mode: Fetch live data
                    let criteria: CriteriaGroup[] = [];
                    const scales = await getEvaluationScales();
                    let questionnaire: Questionnaire | null = null;

                    if (questionnaireId) {
                        questionnaire = await getQuestionnaireById(questionnaireId);
                        if (questionnaire) {
                            criteria = questionnaire.criteria;
                        }
                    } else {
                        criteria = await getCriteriaGroups();
                    }

                    campaignData = {
                        id: 'preview-campaign-id',
                        startDate: new Date(),
                        endDate: new Date(),
                        criteriaSnapshot: criteria,
                        scalesSnapshot: scales,
                        introductionCollaborator: questionnaire?.introductionCollaborator,
                        introductionLeader: questionnaire?.introductionLeader,
                        conclusionCollaborator: questionnaire?.conclusionCollaborator,
                        conclusionLeader: questionnaire?.conclusionLeader,
                        status: 'draft' // Dummy status
                    } as PerformanceEvaluationCampaign;

                    setCampaign(campaignData);

                    // Set mock collaborator
                    setCollaborator({
                        id: 'preview-collab',
                        name: 'Colaborador Exemplo',
                        email: 'colaborador@exemplo.com.br',
                        photoUrl: 'https://i.pravatar.cc/300',
                        positionName: 'Desenvolvedor Senior',
                        departmentName: 'Engenharia',
                        status: 'active',
                        city: 'São Paulo',
                        state: 'SP'
                    });
                } else {
                    // Normal Mode: Fetch existing campaign
                    campaignData = await getPerformanceEvaluationCampaignById(id);
                    setCampaign(campaignData);

                    const targetCollaboratorId = searchParams.get('collaboratorId') || user?.collaboratorId;

                    if (targetCollaboratorId) {
                        const userEmail = user?.email;
                        if (!userEmail && id !== 'preview') {
                            setLoading(false);
                            toast.error("Erro", "Sessão inválida. Verifique seu login.");
                            navigate('/collaborators/evaluations', { replace: true });
                            return;
                        }

                        // Try to find the target collaborator using the current user's email 
                        // Note: If the actual user is an Admin, they still bypass via firestore rules,
                        // but this function provides a targeted lookup anyway.
                        const collaborators = await getPerformanceEvaluationCollaboratorByUserEmail(id, userEmail || '');
                        const target = collaborators.find(c => c.collaboratorId === targetCollaboratorId);

                        if (!target && id !== 'preview') {
                            setLoading(false);
                            toast.error("Erro", "Colaborador não encontrado nesta avaliação.");
                            navigate('/collaborators/evaluations', { replace: true });
                            return;
                        }

                        if (target) {
                            // VERIFY PERMISSIONS
                            const currentUserId = user?.collaboratorId; // Replaced uid with strictly collaboratorId

                            const isSelf = view === 'SELF' && target.collaboratorId && target.collaboratorId === currentUserId;
                            const isLeader = view === 'LEADER' && target.responsibleId && target.responsibleId === currentUserId;

                            if (!isSelf && !isLeader && id !== 'preview') {
                                setLoading(false);
                                toast.error("Acesso Negado", "Você não tem permissão para responder esta avaliação.");
                                navigate('/collaborators/evaluations', { replace: true });
                                return;
                            }

                            setCollaborator({
                                id: target.collaboratorId,
                                name: target.collaboratorName,
                                photoUrl: `https://ui-avatars.com/api/?name=${encodeURIComponent(target.collaboratorName)}&background=random`
                            });

                            // FETCH EXISTING RESPONSE (Draft)
                            const existingResponse = await getEvaluationResponse(id, currentUserId || '', view, target.collaboratorId);
                            if (existingResponse?.status === 'completed') {
                                setLoading(false);
                                toast.info("Avaliação já concluída", "Esta avaliação já foi respondida.");
                                navigate('/collaborators/evaluations', { replace: true });
                                return;
                            }

                            if (id !== 'preview') {
                                try {
                                    await trackEvaluationInviteOpened({
                                        campaignId: id,
                                        collaboratorId: target.collaboratorId,
                                        type: view,
                                    });
                                } catch (trackingError) {
                                    console.warn("Failed to track evaluation opening", trackingError);
                                }
                            }

                            if (existingResponse) {
                                setResponseId(existingResponse.id);
                                setAnswers(existingResponse.answers || {});
                            }
                        }
                    }
                }

            } catch (error) {
                console.error("Failed to load campaign data", error);
                toast.error("Erro", "Falha ao carregar dados da avaliação.");
            } finally {
                setLoading(false);
            }
        };
        fetchCampaignAndResponse();
    }, [id, questionnaireId, toast, searchParams.get('collaboratorId'), searchParams.get('view'), user?.collaboratorId]);

    const handleSave = async (isFinal = false) => {
        if (!campaign) return;

        setIsSaving(true);

        try {
            const currentUserId = user?.collaboratorId; // Standardized to use collaboratorId

            if (!id) return;

            if (!currentUserId && !id.includes('preview')) {
                console.error("No authenticated user found");
                toast.error("Erro", "Sessão inválida. Tente recarregar.");
                setIsSaving(false);
                return;
            }

            const payload = {
                id: responseId,
                campaignId: campaign.id || 'preview',
                collaboratorId: collaborator?.id || 'unknown',
                evaluatorId: currentUserId || 'preview-user-id',
                evaluatorEmail: user?.email || '',
                type: view === 'LEADER' ? 'LEADER' : 'SELF',
                status: isFinal ? 'completed' : 'draft',
                answers: answers,
            };

            await saveEvaluationResponse(payload as any);

            if (isFinal) {
                toast.success("Sucesso", "Avaliação enviada com sucesso!");
                navigate('/collaborators/evaluations');
            }
        } catch (e) {
            console.error("Failed to save", e);
            toast.error("Erro", "Erro ao salvar avaliação.");
        } finally {
            setIsSaving(false);
        }
    };


    const steps = useMemo(() => {
        if (!campaign) return [];
        const flattened: WizardStep[] = [];

        // 1. Introduction
        const introText = view === 'LEADER' ? campaign.introductionLeader : campaign.introductionCollaborator;
        if (introText) {
            flattened.push({
                type: 'INTRODUCTION',
                content: introText,
                indicatorId: 'intro'
            });
        }

        // 2. Questions (Sorted behaviors)
        const sortedGroups = [...campaign.criteriaSnapshot].sort((a, b) => {
            const getOrder = (type?: string) => {
                if (type === 'PROLOGUE') return 0;
                if (type === 'EPILOGUE') return 2;
                return 1;
            };
            return getOrder(a.type) - getOrder(b.type);
        });

        sortedGroups.forEach((group: CriteriaGroup) => {
            group.behaviors.forEach((behavior: CriteriaBehavior) => {
                behavior.indicators.forEach((indicator: CriteriaIndicator) => {
                    if (!indicator.isActive) return;

                    let scale = campaign.scalesSnapshot.find(s => s.id === indicator.scaleId);
                    if (!scale && campaign.scalesSnapshot.length > 0) {
                        scale = campaign.scalesSnapshot[0];
                    }

                    flattened.push({
                        type: 'QUESTION',
                        groupTitle: group.description,
                        behaviorTitle: behavior.description,
                        indicatorId: indicator.id,
                        indicatorDescription: indicator.description,
                        question: (view === 'LEADER' ? indicator.leaderEvaluationQuestion : indicator.selfEvaluationQuestion) || indicator.description,
                        scale: scale
                    });
                });
            });
        });

        // 3. Conclusion
        const conclusionText = view === 'LEADER' ? campaign.conclusionLeader : campaign.conclusionCollaborator;
        if (conclusionText) {
            flattened.push({
                type: 'CONCLUSION',
                content: conclusionText,
                indicatorId: 'conclusion'
            });
        }

        return flattened;
    }, [campaign, view]);

    const handleAnswer = (value: number) => {
        const step = steps[currentStep];
        setAnswers(prev => ({ ...prev, [step.indicatorId]: value }));

        // Auto-advance
        if (currentStep < steps.length - 1) {
            setTimeout(() => {
                setCurrentStep(prev => {
                    const nextStep = prev + 1;
                    return nextStep < steps.length ? nextStep : prev;
                });
            }, 300); // Wait for selection feedback
        }
    };

    const isFirstRender = React.useRef(true);

    // Scroll Management: Scroll content to top when step changes (focus mode)
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        const element = document.getElementById('wizard-content-stage');
        if (element) {
            // Scroll content to start of viewport, effectively pushing header out of view
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [currentStep]);

    const progress = steps.length > 0 ? ((currentStep + 1) / steps.length) * 100 : 0;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    if (!campaign || steps.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 flex-col gap-4">
                <h2 className="text-xl font-semibold text-gray-700">Nenhuma avaliação encontrada ou critérios vazios.</h2>
                <button onClick={() => navigate(-1)} className="text-indigo-600 hover:underline">Voltar</button>
            </div>
        );
    }

    const currentStepData = steps[currentStep];
    const isLastStep = currentStep === steps.length - 1;

    const handlePreviewEdit = async (indicatorId: string, changes: Partial<CriteriaIndicator>) => {
        if (!questionnaireId) return;

        try {
            const questionnaire = await getQuestionnaireById(questionnaireId);
            if (!questionnaire) return;

            let updated = false;

            // Deep clone to safely mutate
            const newCriteria = questionnaire.criteria.map(group => ({
                ...group,
                behaviors: group.behaviors.map(behavior => ({
                    ...behavior,
                    indicators: behavior.indicators.map(indicator => {
                        if (indicator.id === indicatorId) {
                            updated = true;
                            return { ...indicator, ...changes };
                        }
                        return indicator;
                    })
                }))
            }));

            if (updated) {
                // Save to Firestore
                await saveQuestionnaire({ ...questionnaire, criteria: newCriteria });
                toast.success("Sucesso", "Alteração salva!");

                // Update local campaign state to reflect changes immediately
                if (campaign) {
                    setCampaign({
                        ...campaign,
                        criteriaSnapshot: newCriteria
                    });
                }
            }
        } catch (error) {
            console.error("Failed to save preview edit", error);
            toast.error("Erro", "Falha ao salvar alteração.");
        }
    };

    // Animation Variants
    const variants = {
        enter: (direction: number) => ({
            x: direction > 0 ? 50 : -50,
            opacity: 0,
            scale: 0.95,
            filter: 'blur(10px)',
            zIndex: 0
        }),
        center: {
            x: 0,
            opacity: 1,
            scale: 1,
            filter: 'blur(0px)',
            zIndex: 1,
            transition: {
                duration: 0.4,
                ease: "easeInOut" as const
            }
        },
        exit: (direction: number) => ({
            x: direction < 0 ? 50 : -50,
            opacity: 0,
            scale: 0.95,
            filter: 'blur(10px)',
            zIndex: 0,
            transition: {
                duration: 0.3,
                ease: "easeIn" as const
            }
        })
    };

    return (
        <div className="min-h-screen bg-gray-50 font-sans text-gray-900 pb-20" id="wizard-container">
            {collaborator && (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                    <ProfileHeader
                        collaborator={collaborator}
                        onBack={() => navigate(-1)}
                        hideActions
                    />
                </div>
            )}

            {/* Main Content Stage */}
            <div id="wizard-content-stage" className="container mx-auto w-full px-4 sm:px-6 lg:px-8 py-2 relative">
                {/* Progress Bar */}
                <div className="mb-8 px-2">
                    <div className="flex justify-between items-end mb-2">
                        <span className="text-sm font-semibold text-indigo-900">
                            Questão {currentStep + 1} de {steps.length}
                        </span>
                        <span className="text-xs font-bold text-indigo-500 bg-indigo-50 px-2 py-1 rounded-md">
                            {Math.round(progress)}% Concluído
                        </span>
                    </div>
                    <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-indigo-600 transition-all duration-500 ease-out"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>

                <motion.div
                    layout
                    className="relative overflow-visible"
                    transition={{ duration: 0.3 }}
                >
                    <AnimatePresence initial={false} mode="wait" custom={1}>
                        <motion.div
                            key={currentStep}
                            custom={1}
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            className="w-full bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
                            layout
                        >
                            <div className="p-8 md:p-10">
                                {currentStepData.type === 'INTRODUCTION' ? (
                                    <div className="prose prose-indigo max-w-none">
                                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Bem-vindo à sua avaliação</h2>
                                        <ReactMarkdown>{currentStepData.content || ''}</ReactMarkdown>
                                    </div>
                                ) : currentStepData.type === 'CONCLUSION' ? (
                                    <div className="prose prose-indigo max-w-none flex flex-col items-center text-center py-8">
                                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                                            <CheckCircleIcon className="w-10 h-10 text-green-600" />
                                        </div>
                                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Avaliação Concluída!</h2>
                                        <ReactMarkdown>{currentStepData.content || ''}</ReactMarkdown>
                                    </div>
                                ) : (
                                    <>
                                        {/* Question Metadata */}
                                        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
                                                {currentStepData.groupTitle}
                                            </span>
                                            <span className="hidden md:inline text-gray-300">|</span>
                                            <span className="text-sm font-medium text-gray-500">
                                                {currentStepData.behaviorTitle}
                                            </span>

                                            {/* PREVIEW MODE: Scale Selector */}
                                            {id === 'preview' && questionnaireId && (
                                                <div className="ml-auto flex items-center gap-2">
                                                    <span className="text-xs text-gray-500 uppercase font-bold">Escala:</span>
                                                    <select
                                                        className="text-xs border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 py-1"
                                                        value={currentStepData.scale?.id || ''}
                                                        onChange={(e) => {
                                                            const newScaleId = e.target.value;
                                                            handlePreviewEdit(currentStepData.indicatorId, { scaleId: newScaleId });
                                                        }}
                                                    >
                                                        {campaign?.scalesSnapshot.map(scale => (
                                                            <option key={scale.id} value={scale.id}>{scale.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                        </div>

                                        {/* The Question */}
                                        {id === 'preview' && questionnaireId ? (
                                            <div className="mb-8">
                                                <EditableQuestionText
                                                    initialValue={currentStepData.question || ''}
                                                    onSave={(newText) => {
                                                        const updateKey = view === 'LEADER' ? 'leaderEvaluationQuestion' : 'selfEvaluationQuestion';
                                                        handlePreviewEdit(currentStepData.indicatorId, { [updateKey]: newText });
                                                    }}
                                                    key={currentStepData.indicatorId + view} // Force re-mount on step change
                                                />
                                                <p className="text-xs text-gray-400 mt-1 italic">
                                                    Clique no texto para editar. A alteração será salva automaticamente ao sair do campo.
                                                </p>
                                            </div>
                                        ) : (
                                            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 leading-tight mb-8">
                                                {currentStepData.question}
                                            </h2>
                                        )}

                                        {/* Options */}
                                        <div className="grid grid-cols-1 gap-3">
                                            {currentStepData.scale?.options?.sort((a, b) => a.value - b.value).map((option) => {
                                                const isSelected = answers[currentStepData.indicatorId] === option.value;
                                                const label = view === 'LEADER' ? (option.labelLeader || option.label) : (option.labelCollaborator || option.label);
                                                const description = view === 'LEADER' ? (option.descriptionLeader || option.description) : (option.descriptionCollaborator || option.description);

                                                return (
                                                    <button
                                                        key={option.value}
                                                        onClick={() => handleAnswer(option.value)}
                                                        className={`relative w-full text-left p-4 md:p-5 rounded-xl border-2 transition-all duration-200 group
                                                            ${isSelected
                                                                ? 'border-indigo-600 bg-indigo-50/50 shadow-sm ring-1 ring-indigo-600 z-10'
                                                                : 'border-gray-100 hover:border-indigo-200 hover:bg-gray-50'
                                                            }
                                                        `}
                                                    >
                                                        <div className="flex items-start gap-4">
                                                            <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors mt-0.5
                                                                ${isSelected ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300 group-hover:border-indigo-400'}
                                                            `}>
                                                                {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex justify-between">
                                                                    <span className={`font-bold ${isSelected ? 'text-indigo-900' : 'text-gray-700'}`}>{label}</span>
                                                                </div>
                                                                {description && (
                                                                    <p className={`text-sm mt-1 leading-relaxed ${isSelected ? 'text-indigo-800/80' : 'text-gray-500'}`}>
                                                                        {description}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </button>
                                                );
                                            }) || (
                                                    <div className="p-4 bg-yellow-50 text-yellow-800 rounded-lg text-sm">
                                                        Escala de opções não configurada para esta questão.
                                                    </div>
                                                )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </motion.div>

                {/* Navigation Actions */}
                <div className="mt-8 flex justify-between items-center px-2">
                    {currentStepData.type === 'CONCLUSION' ? (
                        <div className="w-full flex justify-center">
                            <button onClick={() => navigate(-1)} className="text-indigo-600 hover:underline">Voltar ao início</button>
                        </div>
                    ) : (
                        <>
                            <button
                                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                                disabled={currentStep === 0}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors
                                    ${currentStep === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-indigo-600 hover:bg-white'}
                                `}
                            >
                                <ArrowLeftIcon className="w-4 h-4" />
                                Anterior
                            </button>

                            <button
                                onClick={() => {
                                    if (!isLastStep) {
                                        setCurrentStep(currentStep + 1);
                                    } else {
                                        handleSave(true);
                                    }
                                }}
                                disabled={
                                    (currentStepData.type === 'QUESTION' && (!answers[currentStepData.indicatorId] || isSaving)) ||
                                    isSaving
                                }
                                className={`flex items-center gap-2 px-8 py-3 rounded-full font-bold shadow-lg shadow-indigo-200 transition-all 
                                     ${(currentStepData.type === 'QUESTION' && !answers[currentStepData.indicatorId]) || isSaving
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                                        : 'bg-indigo-600 text-white hover:bg-indigo-700 hover:scale-105 active:scale-95'
                                    }
                                 `}
                            >
                                {isSaving ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Salvando...
                                    </>
                                ) : (
                                    currentStepData.type === 'INTRODUCTION' ? 'Começar' :
                                        isLastStep ? 'Finalizar' : 'Próximo'
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
